import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Play, Crown, Lock } from "lucide-react";
import { api, resolveFileUrl, contentToQueue } from "../api";
import { usePlayer, useAuth } from "../contexts";
import { PageHeader, EmptyState } from "../components/ui-bits";

const META = {
  mixtapes: {
    kicker: "DEEP CRATE",
    title: "MIXTAPES",
    sub: "Curated tapes, official drops, and underground heat — straight from the source.",
  },
  singles: {
    kicker: "ON ROTATION",
    title: "SINGLES",
    sub: "The hottest cuts in heavy rotation across the platform.",
  },
};

export default function MusicListPage({ ctype }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | official | indy
  const { playList } = usePlayer();
  const { culturePassActive } = useAuth();
  const navigate = useNavigate();
  const meta = META[ctype] || { kicker: ctype.toUpperCase(), title: ctype.toUpperCase() };

  useEffect(() => {
    setLoading(true);
    api.get(`/content/${ctype}`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [ctype]);

  const filtered = items.filter((i) =>
    filter === "all" ? true : filter === "official" ? i.is_official : !i.is_official
  );

  const hasPlayable = (x) => (Array.isArray(x.tracks) && x.tracks.some((t) => t?.url)) || !!x.file_url;

  const handlePlay = (item) => {
    if (item.early_access && !culturePassActive) {
      navigate("/culture-pass");
      return;
    }
    // Expand every playable item on the page into one flat queue so users can
    // let the whole list play back-to-back. Multi-track mixtapes contribute all
    // their tracks in order via contentToQueue.
    const playable = filtered.filter(
      (x) => hasPlayable(x) && (!x.early_access || culturePassActive),
    );
    const queue = playable.flatMap((x) => contentToQueue(x, ctype));
    const first = contentToQueue(item, ctype)[0];
    const idx = first ? queue.findIndex((t) => t.id === first.id) : -1;
    playList(queue, Math.max(0, idx));
    api.post(`/content/${ctype}/${item.id}/play`).catch(() => {});
  };

  return (
    <div className="pb-32">
      <PageHeader
        kicker={meta.kicker}
        title={meta.title}
        sub={meta.sub}
        right={
          <div className="flex gap-2" data-testid="music-filter">
            {["all", "official", "indy"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`chip ${filter === f ? "chip-accent" : ""}`}
                data-testid={`filter-${f}`}
              >
                {f.toUpperCase()}
              </button>
            ))}
          </div>
        }
      />
      <div className="divider-tape" />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-10">
        {loading ? (
          <p className="font-mono-label text-[10px] text-neutral-500" data-testid={`${ctype}-loading`}>Loading {ctype}…</p>
        ) : filtered.length === 0 ? (
          <EmptyState>No {ctype} match this filter.</EmptyState>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid={`${ctype}-grid`}>
            {filtered.map((m) => (
              <div key={m.id} className="card-brut group" data-testid={`${ctype}-card-${m.id}`}>
                <div className="relative aspect-square overflow-hidden">
                  <Link to={`/${ctype}/${m.id}`}>
                    {m.cover_image ? (
                      <img src={resolveFileUrl(m.cover_image)} alt={m.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full bg-neutral-950 flex items-center justify-center font-display text-3xl text-neutral-700 uppercase p-4 text-center" data-testid={`cover-placeholder-${m.id}`}>{m.title}</div>
                    )}
                  </Link>
                  {hasPlayable(m) && (
                    <button
                      onClick={() => handlePlay(m)}
                      className="absolute bottom-3 right-3 w-11 h-11 bg-[var(--accent)] hover:bg-[var(--accent-hover)] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`play-${m.id}`}
                      aria-label={`Play ${m.title}`}
                    >
                      {m.early_access && !culturePassActive ? <Lock size={14} /> : <Play size={16} />}
                    </button>
                  )}
                  <div className="absolute top-3 left-3 flex flex-col gap-1">
                    {m.is_official && <span className="chip chip-accent">OFFICIAL</span>}
                    {m.early_access && (
                      <span className="chip chip-accent inline-flex items-center gap-1" data-testid={`early-access-${m.id}`}>
                        <Crown size={10} /> EARLY ACCESS
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-4 border-t border-neutral-800">
                  <Link to={`/${ctype}/${m.id}`} className="block">
                    <div className="font-headline text-base uppercase truncate">{m.title}</div>
                    <div className="font-mono-label text-[10px] text-neutral-400 mt-1 flex justify-between gap-2">
                      <span className="truncate" data-testid={`artist-${m.id}`}>{m.artist}</span>
                      <span>{m.year}</span>
                    </div>
                  </Link>
                  <Link to={`/${ctype}/${m.id}`} className="mt-3 flex items-center justify-center gap-2 py-2 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white font-mono-label text-[10px] uppercase transition-colors" data-testid={`listen-now-${m.id}`}>
                    <Play size={11} fill="currentColor" /> Listen Now
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
